import { useMemo, useState, type FormEvent } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { downloadIcs, guestFetch, guestLiveApi, WEBINAR_STATUS_LABEL } from '../lib/live';
import { Button, Card, ErrorText, Field, Input, Pill } from '../components/ui';
import { WebinarRoom } from '../components/WebinarRoom';

interface Preview {
  webinar: { title: string; description: string | null; startsAt: string; durationMinutes: number; status: string; host: string };
  disclaimer: { id: string; title: string; body: string; version: number } | null;
}
interface GuestView {
  guest: { id: string; name: string; email: string };
  webinar: { id: string; title: string; description: string | null; startsAt: string; durationMinutes: number; status: 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED'; layout: 'CHART_WEBCAM' | 'SPEAKER' | 'SIDE_BY_SIDE'; host: string; coHosts: string[] };
  capacity: { max: number; inRoom: number };
  livekit: { configured: boolean };
}

const tz = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
const when = (d: string) => new Intl.DateTimeFormat('en-GB', { timeZone: tz(), weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }).format(new Date(d));
const storeKey = (token: string) => `vtf-guest-${token}`;
const read = (token: string) => {
  try {
    return sessionStorage.getItem(storeKey(token));
  } catch {
    return null;
  }
};

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-screen w-full max-w-3xl flex-col gap-4 px-4 py-6">
      <header className="flex items-center justify-between gap-3">
        <strong className="text-lg">K5 Trading · Live webinar</strong>
        <Pill>Guest</Pill>
      </header>
      {children}
    </main>
  );
}

/** Guest landing: the invite link shows the webinar and asks for name, email and the disclaimer; then the guest is in. */
export function GuestWebinar() {
  const { token = '' } = useParams();
  const [session, setSession] = useState<string | null>(() => read(token));
  const live = useQuery({ queryKey: ['guest-view', token, session], queryFn: () => guestFetch<GuestView>('/webinar', session), enabled: !!session, refetchInterval: 20_000, retry: false });
  const preview = useQuery({ queryKey: ['guest-preview', token], queryFn: () => guestFetch<Preview>(`/invite/${encodeURIComponent(token)}`, null), retry: false, enabled: !session || live.isError });
  const [form, setForm] = useState({ name: '', email: '', accept: false, website: '' });
  const register = useMutation({
    mutationFn: () => guestFetch<{ sessionToken: string }>(`/invite/${encodeURIComponent(token)}/register`, null, { body: { name: form.name, email: form.email, acceptedPolicyId: preview.data!.disclaimer!.id, website: form.website } }),
    onSuccess: (r) => {
      try {
        sessionStorage.setItem(storeKey(token), r.sessionToken);
      } catch {
        /* the session still works for this page view */
      }
      setSession(r.sessionToken);
    },
  });
  const liveApi = useMemo(() => (session ? guestLiveApi(session) : null), [session]);

  if (session && live.data && liveApi) {
    const w = live.data.webinar;
    return (
      <Frame>
        <Card className="flex flex-col gap-2 p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <h1 className="m-0 text-xl font-semibold">{w.title}</h1>
            <Pill tone={w.status === 'LIVE' ? 'up' : 'accent'}>{WEBINAR_STATUS_LABEL[w.status]}</Pill>
          </div>
          <p className="m-0 text-sm text-sub">{when(w.startsAt)} · {w.durationMinutes} min · hosted by {w.host}</p>
          {w.description && <p className="m-0 whitespace-pre-line text-ink2">{w.description}</p>}
          <p className="m-0 text-sm text-muted">Registered as {live.data.guest.name} ({live.data.guest.email}).</p>
          <div><Button onClick={() => void downloadIcs(w.id, '/api/v1/guest/webinar/ics', { 'x-webinar-guest': session })}>Add to calendar (.ics)</Button></div>
        </Card>
        {!live.data.livekit.configured && <p role="status" className="m-0 rounded-lg bg-warn-bg px-3 py-2 text-sm text-warn">Live video isn’t set up yet.</p>}
        <WebinarRoom id={w.id} live={liveApi} guestSession={session} layout={w.layout} canJoinNow={w.status === 'LIVE'} joinHint={w.status === 'SCHEDULED' ? 'The host hasn’t started yet. Keep this page open; it updates when they go live.' : 'This webinar is over.'} />
      </Frame>
    );
  }
  if (session && live.isError) {
    const status = (live.error as { status?: number }).status;
    if (status === 403) return <Frame><Card className="p-4"><h1 className="m-0 mb-2 text-xl font-semibold">This webinar has ended</h1><p className="m-0 text-sub">Thanks for joining. Your guest access has ended with it.</p></Card></Frame>;
  }
  if (preview.isLoading || (session && live.isLoading)) return <Frame><Card className="p-4 text-sub">Loading…</Card></Frame>;
  if (preview.error || !preview.data) return <Frame><Card className="p-4"><h1 className="m-0 mb-2 text-xl font-semibold">This invite isn’t available</h1><p className="m-0 text-sub">{(preview.error as Error | null)?.message ?? 'The link is not valid.'}</p></Card></Frame>;
  const p = preview.data;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    register.mutate();
  };
  return (
    <Frame>
      <Card className="flex flex-col gap-2 p-4">
        <h1 className="m-0 text-xl font-semibold">{p.webinar.title}</h1>
        <p className="m-0 text-sm text-sub">{when(p.webinar.startsAt)} · {p.webinar.durationMinutes} min · hosted by {p.webinar.host}</p>
        {p.webinar.description && <p className="m-0 whitespace-pre-line text-ink2">{p.webinar.description}</p>}
      </Card>
      <Card>
        <form onSubmit={submit} className="flex flex-col gap-3 p-4">
          <h2 className="m-0 text-base font-semibold">Register to join</h2>
          <Field label="Your name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoComplete="name" required minLength={2} /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" required /></Field>
          <div aria-hidden="true" className="absolute -left-[9999px]"><label>Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></label></div>
          {p.disclaimer ? (
            <>
              <details className="rounded-lg bg-raised px-3 py-2 text-sm">
                <summary className="min-h-9 cursor-pointer font-semibold">{p.disclaimer.title}</summary>
                <p className="m-0 mt-2 whitespace-pre-line text-ink2">{p.disclaimer.body}</p>
              </details>
              <label className="flex min-h-11 items-start gap-2 text-sm">
                <input type="checkbox" checked={form.accept} onChange={(e) => setForm({ ...form, accept: e.target.checked })} className="mt-1" />
                I have read and accept the {p.disclaimer.title.toLowerCase()}.
              </label>
            </>
          ) : (
            <p role="status" className="m-0 text-sm text-warn">Registration isn’t open yet. Please ask your host to try again later.</p>
          )}
          <ErrorText error={register.error} />
          <div><Button variant="primary" type="submit" disabled={register.isPending || !p.disclaimer || !form.accept || form.name.trim().length < 2 || !form.email}>{register.isPending ? 'Registering…' : 'Register and continue'}</Button></div>
        </form>
      </Card>
    </Frame>
  );
}
